import { SEOHead } from "@/components/seo";
import { SmoothLink as Link } from "@/components/navigation";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/shared/PageHeader";
import { BreadcrumbJsonLd, FAQJsonLd, AnswerCapsule, OrganizationJsonLd } from "@/components/seo";
import { COMPANY, getWhatsAppLink } from "@/config/company";
import { CTASection } from "@/components/home/CTASection";
import { 
  CheckCircle, 
  Globe, 
  DollarSign, 
  Shield, 
  Clock, 
  Users,
  Plane,
  Building2,
  Stethoscope,
  Heart,
  MessageCircle,
  ArrowRight,
  Star
} from "lucide-react";

const guideFAQs = [
  {
    question: "What is medical tourism?",
    answer: "Medical tourism refers to traveling to another country to receive medical treatment. Patients typically seek treatment abroad for cost savings (often 50-80% less), shorter wait times, access to procedures not available in their home country, or to combine treatment with travel."
  },
  {
    question: "Why is India a top destination for medical tourism?",
    answer: "India is a global leader in medical tourism due to its combination of world-class JCI/NABH accredited hospitals, internationally trained doctors, advanced medical technology, English-speaking staff, and costs that are 60-80% lower than the US/UK. India performs over 2 million medical procedures for international patients annually."
  },
  {
    question: "Is it safe to get medical treatment in India?",
    answer: "Yes, medical treatment in India is safe when you choose accredited facilities. Top Indian hospitals hold JCI (Joint Commission International) and NABH accreditation—the same standards as leading US hospitals. Many Indian doctors are trained in the US, UK, and Europe, with 20-30+ years of experience."
  },
  {
    question: "How much can I save with medical tourism in India?",
    answer: "Patients typically save 60-80% compared to US prices. For example: dental implants ($400-800 vs $3,000-5,000), heart bypass surgery ($8,000-15,000 vs $100,000-150,000), knee replacement ($6,000-10,000 vs $50,000-70,000). These savings include hospital stay, surgeon fees, and post-operative care."
  },
  {
    question: "What is included in a medical tourism package?",
    answer: "A comprehensive medical tourism package typically includes: initial consultation and treatment planning, hospital and doctor selection, visa assistance and travel coordination, airport pickup and local transportation, accommodation near the hospital, a dedicated care coordinator, interpreter services if needed, and post-treatment follow-up support."
  },
  {
    question: "How long do I need to stay in India for treatment?",
    answer: "Duration varies by procedure. Dental implants: 5-10 days. Knee/hip replacement: 10-14 days. Heart bypass: 14-21 days. Many procedures allow you to combine treatment with recovery and even sightseeing, making your medical journey a complete experience."
  }
];

const treatmentCategories = [
  { name: "Dental Treatments", href: "/treatments/dental/", icon: "🦷", description: "Implants, veneers, smile makeovers" },
  { name: "Cardiac Surgery", href: "/treatments/cardiac/", icon: "❤️", description: "Bypass, angioplasty, valve replacement" },
  { name: "Orthopedic Surgery", href: "/treatments/orthopedics/", icon: "🦴", description: "Knee, hip, spine surgery" },
  { name: "Oncology", href: "/treatments/oncology/", icon: "🎗️", description: "Cancer treatment, transplants" },
  { name: "Fertility", href: "/treatments/fertility/", icon: "👶", description: "IVF, IUI, fertility preservation" },
  { name: "Cosmetic Surgery", href: "/treatments/cosmetic/", icon: "✨", description: "Rhinoplasty, liposuction, facelift" },
];

const trustFactors = [
  { icon: Shield, title: "JCI/NABH Accredited", description: "Same standards as top US hospitals" },
  { icon: Users, title: "2M+ International Patients", description: "India treats annually" },
  { icon: Star, title: "World-Class Doctors", description: "US/UK trained specialists" },
  { icon: DollarSign, title: "60-80% Cost Savings", description: "Compared to Western countries" },
  { icon: Clock, title: "No Wait Times", description: "Treatment within days, not months" },
  { icon: Globe, title: "English Speaking", description: "All major hospitals" },
];

export default function MedicalTourismGuide() {
  const { trackCTAClick, trackWhatsAppClick } = useAnalyticsContext();
  const breadcrumbItems = [
    { name: "Home", url: `${COMPANY.website}/` },
    { name: "Medical Tourism in India", url: `${COMPANY.website}/medical-tourism-india` },
  ];

  return (
    <div className="min-h-screen bg-background">
            <SEOHead
        title={"Complete Medical Tourism Guide to India 2025 | TrustedHeal"}
        description={"Everything you need to know about medical tourism in India: costs, safety, best hospitals, visa process, and patient experiences. Your complete guide to affordable world-class healthcare."}
        canonicalPath={"/medical-tourism-india/"}
        ogType="article"
      />

      <OrganizationJsonLd />
      <BreadcrumbJsonLd items={breadcrumbItems} />
      <FAQJsonLd faqs={guideFAQs} />



      <main className="pt-24">
        {/* Hero Section */}
        <PageHeader 
          title="Your Medical Journey to India"
          description="A complete guide to planning your medical trip, from visa requirements to post-treatment care"
          badge={
            <span className="inline-block bg-accent/10 border border-accent/20 text-accent px-4 py-1.5 rounded-full text-sm font-medium">
              Complete Guide 2025
            </span>
          }
          className="mb-16"
        >
          <div className="flex flex-wrap justify-center gap-6 text-left max-w-2xl mx-auto mt-8 mb-8">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-muted-foreground font-medium">Save 60-80% on costs</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-muted-foreground font-medium">Visa & Travel Support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-muted-foreground font-medium">JCI-accredited care</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="rounded-full px-8" asChild>
              <a 
                href="#planning-steps" 
                onClick={() => trackCTAClick('Start Planning', 'guide_hero', 'primary')}
              >
                Start Planning
              </a>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full px-8" asChild>
              <Link 
                to="/contact/"
                onClick={() => trackCTAClick('Speak to an Expert', 'guide_hero', 'secondary')}
              >
                Speak to an Expert
              </Link>
            </Button>
          </div>
        </PageHeader>

        {/* Table of Contents */}
        <section className="py-12 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-2xl font-bold text-secondary mb-6">In This Guide</h2>
              <nav className="grid md:grid-cols-2 gap-3">
                {[
                  "What is Medical Tourism?",
                  "Why Choose India?",
                  "Treatments Available",
                  "Top Hospitals in India",
                  "Cost Comparison",
                  "How TrustedHeal Works",
                  "Planning Your Journey",
                  "Frequently Asked Questions",
                ].map((item, i) => (
                  <a 
                    key={i}
                    href={`#section-${i + 1}`}
                    className="flex items-center gap-3 p-3 bg-card rounded-lg hover:shadow-soft transition-shadow text-foreground hover:text-primary"
                  >
                    <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                      {i + 1}
                    </span>
                    {item}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </section>

        {/* Section 1: What is Medical Tourism */}
        <section id="section-1" className="py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                1. What is Medical Tourism?
              </h2>
              
              <AnswerCapsule
                term="Medical Tourism in India"
                definition="The practice of international patients traveling to India for healthcare, combining world-class JCI-accredited treatment with 60-80% cost savings compared to Western nations."
                statistic="Avg Savings: 70% | JCI Hospitals: 38+"
                source="Ministry of Health & Family Welfare (2025)"
                className="mb-8"
              />

              <div className="prose prose-lg max-w-none text-foreground/80">
                <p>
                  <strong>Medical tourism</strong> (also called health tourism or medical travel) refers to the practice of 
                  traveling to another country to receive medical, dental, or surgical care. It has become a global 
                  industry worth over $100 billion annually, with millions of patients crossing borders for treatment each year.
                </p>
                <p>
                  Patients choose medical tourism for several reasons:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Cost savings:</strong> Treatments can cost 50-80% less than in the US, UK, or Europe</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Shorter wait times:</strong> Get treatment in days, not months or years</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Access to procedures:</strong> Some treatments may not be available or approved in your country</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Quality of care:</strong> Top medical tourism destinations have world-class hospitals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Privacy:</strong> Some patients prefer discretion for certain procedures</span>
                  </li>
                </ul>
                <p>
                  The most popular medical tourism destinations include India, Thailand, Mexico, Turkey, and Singapore. 
                  India has emerged as the global leader due to its unique combination of quality, cost, and English-speaking 
                  medical professionals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Why Choose India */}
        <section id="section-2" className="py-16 lg:py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                2. Why Choose India for Medical Treatment?
              </h2>
              <p className="text-lg text-foreground/80 mb-8">
                India has become the world's leading destination for medical tourism, treating over 
                <strong> 2 million international patients annually</strong>. Here's why patients from the US, UK, 
                Canada, Australia, and the Middle East choose India:
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                {trustFactors.map((factor, i) => (
                  <Card key={i} className="p-6 text-center hover:shadow-hover transition-shadow">
                    <factor.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                    <h3 className="font-display font-semibold text-secondary mb-2">{factor.title}</h3>
                    <p className="text-sm text-muted-foreground">{factor.description}</p>
                  </Card>
                ))}
              </div>

              <div className="prose prose-lg max-w-none text-foreground/80">
                <h3 className="font-display text-xl font-semibold text-secondary">World-Class Hospital Infrastructure</h3>
                <p>
                  India's top hospitals are equipped with the latest medical technology, including robotic surgery systems, 
                  advanced imaging (PET-CT, 3T MRI), proton therapy for cancer, and AI-assisted diagnostics. Many Indian 
                  hospitals have more JCI accreditations than entire countries.
                </p>

                <h3 className="font-display text-xl font-semibold text-secondary">Internationally Trained Doctors</h3>
                <p>
                  Indian doctors are renowned worldwide. Many have trained at prestigious institutions in the US 
                  (Mayo Clinic, Cleveland Clinic, Johns Hopkins), UK (Oxford, Cambridge), and Europe. They bring 
                  global best practices while offering personalized, patient-centric care that Western patients often 
                  find lacking in their home countries.
                </p>

                <h3 className="font-display text-xl font-semibold text-secondary">No Language Barrier</h3>
                <p>
                  English is widely spoken in India, especially in healthcare settings. All major hospitals have 
                  English-speaking staff, and many also offer interpreter services for Arabic, Russian, French, 
                  and other languages.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Treatments Available */}
        <section id="section-3" className="py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                3. Medical Treatments Available in India
              </h2>
              <p className="text-lg text-foreground/80 mb-8">
                India offers virtually every medical specialty and procedure. The most popular treatments 
                for international patients include:
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {treatmentCategories.map((cat) => (
                  <Link 
                    key={cat.name}
                    to={cat.href}
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-soft transition-all group"
                  >
                    <span className="text-3xl">{cat.icon}</span>
                    <div>
                      <h3 className="font-display font-semibold text-secondary group-hover:text-primary transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{cat.description}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="bg-accent/10 border border-accent/20 rounded-xl p-6">
                <h3 className="font-display text-xl font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-primary" />
                  Most Popular Procedures
                </h3>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-foreground/80">
                   <Link 
                    to="/treatments/dental/all-on-4-implants/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('All-on-4 Implants', 'guide_popular', 'link')}
                  >
                    → All-on-4 Dental Implants
                  </Link>
                  <Link 
                    to="/treatments/cardiac/heart-bypass-cabg/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('Heart Bypass Surgery', 'guide_popular', 'link')}
                  >
                    → Heart Bypass Surgery (CABG)
                  </Link>
                  <Link 
                    to="/treatments/orthopedics/knee-replacement/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('Total Knee Replacement', 'guide_popular', 'link')}
                  >
                    → Total Knee Replacement
                  </Link>
                  <Link 
                    to="/treatments/orthopedics/hip-replacement/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('Total Hip Replacement', 'guide_popular', 'link')}
                  >
                    → Total Hip Replacement
                  </Link>
                  <Link 
                    to="/treatments/dental/dental-implants/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('Single Dental Implants', 'guide_popular', 'link')}
                  >
                    → Single Dental Implants
                  </Link>
                  <Link 
                    to="/treatments/cardiac/angioplasty/" 
                    className="hover:text-primary transition-colors"
                    onClick={() => trackCTAClick('Angioplasty with Stents', 'guide_popular', 'link')}
                  >
                    → Angioplasty with Stents
                  </Link>
                </div>
                <div className="mt-4 pt-4 border-t border-accent/20">
                  <Link to="/treatments/" className="text-primary font-medium hover:underline flex items-center gap-1">
                    View all treatments available in India
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Top Hospitals */}
        <section id="section-4" className="py-16 lg:py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                4. Top Hospitals in India for International Patients
              </h2>
              <p className="text-lg text-foreground/80 mb-8">
                TrustedHeal partners exclusively with <strong>JCI and NABH accredited hospitals</strong>—the 
                same international standards followed by top US and European hospitals. Our partner network includes:
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { name: "Medanta - The Medicity", city: "Gurugram", specialty: "Multi-specialty, Cardiac, Transplants" },
                  { name: "Apollo Hospitals", city: "Delhi, Mumbai, Chennai", specialty: "Multi-specialty, Oncology" },
                  { name: "Fortis Healthcare", city: "Gurugram, Mumbai, Bangalore", specialty: "Cardiac, Orthopedics" },
                  { name: "Max Super Specialty", city: "Delhi NCR", specialty: "Multi-specialty, Neurology" },
                  { name: "Kokilaben Ambani Hospital", city: "Mumbai", specialty: "Robotic Surgery, Oncology" },
                  { name: "Narayana Health", city: "Bangalore", specialty: "Cardiac (Affordable Excellence)" },
                ].map((hospital) => (
                  <div key={hospital.name} className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border/50">
                    <Building2 className="w-8 h-8 text-primary flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-secondary">{hospital.name}</h3>
                      <p className="text-sm text-muted-foreground">{hospital.city} • {hospital.specialty}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">JCI Accredited</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/hospitals/" 
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                Explore all partner hospitals
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Section 5: Cost Comparison */}
        <section id="section-5" className="py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                5. Cost Comparison: India vs USA/UK
              </h2>

              <AnswerCapsule
                term="Cost of Medical Treatment in India"
                definition="Medical procedures in India cost 60-90% less than in the US or UK due to lower labor costs and government price caps on implants, without compromising quality in JCI-accredited facilities."
                statistic="Heart Bypass: $10k (India) vs $130k (USA)"
                source="Medical Tourism Association Index 2025"
                className="mb-8"
              />

              <p className="text-lg text-foreground/80 mb-8">
                The cost savings with medical tourism in India are substantial. Here's a comparison of 
                common procedures (prices in USD):
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-card rounded-xl overflow-hidden shadow-soft">
                  <thead className="bg-secondary text-primary-foreground">
                    <tr>
                      <th className="px-4 py-4 text-left font-display">Procedure</th>
                      <th className="px-4 py-4 text-right font-display">India</th>
                      <th className="px-4 py-4 text-right font-display">USA</th>
                      <th className="px-4 py-4 text-right font-display">Savings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {[
                      { name: "Dental Implant (Single)", india: "$400-800", usa: "$3,000-5,000", savings: "85%" },
                      { name: "All-on-4 Implants", india: "$5,000-8,000", usa: "$25,000-40,000", savings: "80%" },
                      { name: "Heart Bypass (CABG)", india: "$8,000-15,000", usa: "$100,000-150,000", savings: "90%" },
                      { name: "Knee Replacement", india: "$6,000-10,000", usa: "$50,000-70,000", savings: "85%" },
                      { name: "Hip Replacement", india: "$7,000-12,000", usa: "$50,000-65,000", savings: "85%" },
                      { name: "IVF Treatment", india: "$3,000-5,000", usa: "$15,000-25,000", savings: "80%" },
                      { name: "Angioplasty + Stent", india: "$3,000-6,000", usa: "$30,000-50,000", savings: "90%" },
                    ].map((row) => (
                      <tr key={row.name} className="hover:bg-muted/50">
                        <td className="px-4 py-3 font-medium text-secondary">{row.name}</td>
                        <td className="px-4 py-3 text-right text-primary font-semibold">{row.india}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{row.usa}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="bg-accent/20 text-accent px-2 py-1 rounded text-sm font-medium">
                            {row.savings}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-muted-foreground mb-6">
                * India prices include hospital stay, surgeon fees, anesthesia, and post-operative care. 
                Prices may vary based on hospital, complexity, and implant type.
              </p>

              <Link 
                to="/compare-costs/" 
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                View detailed cost comparisons for all procedures
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Section 6: How TrustedHeal Works */}
        <section id="section-6" className="py-16 lg:py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6">
                6. How TrustedHeal Works
              </h2>
              <p className="text-lg text-foreground/80 mb-8">
                <strong>TrustedHeal is a medical tourism facilitation platform</strong>—not a hospital. We connect 
                international patients with verified, accredited hospitals and doctors in India, managing your 
                entire treatment journey from consultation to recovery.
              </p>

              <div className="space-y-6">
                {[
                  { 
                    step: 1, 
                    title: "Share Your Medical Needs", 
                    description: "Submit your medical reports via our website, WhatsApp, or by speaking with our AI assistant Dr. Maya. We review your case within 24 hours.",
                    icon: MessageCircle 
                  },
                  { 
                    step: 2, 
                    title: "Receive Treatment Plan", 
                    description: "Get a personalized treatment plan with hospital options, doctor profiles, cost estimates, and timeline—all within 24-48 hours.",
                    icon: Stethoscope 
                  },
                  { 
                    step: 3, 
                    title: "Confirm & Book", 
                    description: "Once you approve the plan, we handle visa assistance, appointment scheduling, and hospital coordination. No hidden fees.",
                    icon: CheckCircle 
                  },
                  { 
                    step: 4, 
                    title: "Travel to India", 
                    description: "We arrange airport pickup, accommodation near the hospital, and assign a dedicated care coordinator who speaks your language.",
                    icon: Plane 
                  },
                  { 
                    step: 5, 
                    title: "Receive Treatment", 
                    description: "Get world-class treatment with your coordinator by your side. We handle all hospital logistics and keep your family updated.",
                    icon: Heart 
                  },
                  { 
                    step: 6, 
                    title: "Return Home Healthy", 
                    description: "We provide a complete treatment summary, medication plan, and post-treatment follow-up support. Our care continues after you leave.",
                    icon: Globe 
                  },
                ].map((step) => (
                  <div key={step.step} className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-display text-lg font-semibold text-secondary mb-1">{step.title}</h3>
                      <p className="text-foreground/70">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <Button asChild size="lg" className="rounded-pill">
                  <Link 
                    to="/contact/"
                    onClick={() => trackCTAClick('Start Your Journey', 'guide_how_it_works', 'primary')}
                  >
                    Start Your Journey
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-pill">
                  <a 
                    href={getWhatsAppLink("I'd like to discuss medical treatment options in India")} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick('guide_how_it_works')}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp Us
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Planning Your Journey */}
        <section id="section-7" className="py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 id="planning-steps" className="font-display text-3xl md:text-4xl font-bold text-secondary mb-6 scroll-mt-28">
                7. Planning Your Medical Tourism Journey
              </h2>
              
              <div className="prose prose-lg max-w-none text-foreground/80">
                <h3 className="font-display text-xl font-semibold text-secondary">Visa Requirements</h3>
                <p>
                  India offers a <strong>Medical Visa (M Visa)</strong> specifically for patients seeking treatment. 
                  It allows multiple entries and can be valid for up to 1 year. Companions can apply for a 
                  <strong> Medical Attendant Visa</strong>. TrustedHeal provides visa assistance including invitation 
                  letters from the hospital.
                </p>

                <h3 className="font-display text-xl font-semibold text-secondary">Best Time to Visit</h3>
                <p>
                  India has year-round medical tourism. However, the most comfortable weather is from October to 
                  March. Major cities like Delhi, Mumbai, Bangalore, and Chennai have international airports with 
                  direct flights from most countries.
                </p>

                <h3 className="font-display text-xl font-semibold text-secondary">What to Bring</h3>
                <ul>
                  <li>All medical records, test reports, and imaging (X-rays, MRI, CT scans)</li>
                  <li>Current medication list with dosages</li>
                  <li>Passport with at least 6 months validity</li>
                  <li>Comfortable clothing for recovery period</li>
                  <li>Power adapter (India uses Type C/D plugs)</li>
                </ul>

                <h3 className="font-display text-xl font-semibold text-secondary">Payment Methods</h3>
                <p>
                  Most hospitals accept international credit cards, wire transfers, and sometimes cryptocurrency. 
                  TrustedHeal provides transparent cost breakdowns upfront with no surprise charges. Payment plans 
                  are available for certain procedures.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: FAQ */}
        <section id="section-8" className="py-16 lg:py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary mb-8">
                8. Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                {guideFAQs.map((faq, i) => (
                  <div key={i} className="bg-card rounded-xl p-6 shadow-soft">
                    <h3 className="font-display text-lg font-semibold text-secondary mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-foreground/80 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Sources & Citations */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-2xl font-bold text-secondary mb-4">
                Sources & Citations
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Key claims on accreditation and medical tourism volume reference the sources below.
                Procedure cost examples are indicative and vary by hospital, case complexity, and implant type.
              </p>
              <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-2">
                <li>
                  <a
                    href="https://tourism.gov.in/sites/default/files/2024-07/PIB2036816.pdf"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    Ministry of Tourism (PIB): medical tourist arrivals (BOI data)
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.jointcommissioninternational.org/what-we-offer/accreditation/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    JCI accreditation overview (Joint Commission International)
                  </a>
                </li>
                <li>
                  <a
                    href="https://nabh.co/faq-nabh/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    NABH overview and recognition (NABH)
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.scientificdentalclinic.com/dental-implants-cost-in-us-and-india/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    Example dental implant price ranges (India vs USA)
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <CTASection />
      </main>


    </div>
  );
}
